import {
  CellClickedEvent,
  ColDef,
  FilterChangedEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  ISetFilter,
  ModuleRegistry,
  PaginationChangedEvent,
  SelectionChangedEvent,
  SetFilterModel,
  SetFilterValuesFuncParams,
  SideBarDef,
  SortChangedEvent,
  ValueGetterParams
} from "@ag-grid-community/core";
import {AgGridReact} from "@ag-grid-community/react";
import {ColumnsToolPanelModule} from "@ag-grid-enterprise/column-tool-panel";
import {ServerSideRowModelModule} from "@ag-grid-enterprise/server-side-row-model";
import log from "loglevel";
import {SetFilterModule} from "@ag-grid-enterprise/set-filter";
import {ApolloError, ApolloQueryResult, useApolloClient} from "@apollo/client";
import {Button, ControlGroup, Icon, Intent, Text} from "@blueprintjs/core";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {subDays} from "date-fns";
import {RuleProperties} from "json-rules-engine";
import {isEqual} from "lodash";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useAppContext} from "../../ApplicationContext";
import {Job, JobAttributeFilter, OrderRequirement, useGetDistinctValuesLazyQuery} from "../../generated/graphql";
import {useFeatureFlagContext} from "../../providers/FeatureFlagProvider";
import {extractJsonPref, extractSimplePref, usePreferenceContext} from "../../providers/PreferenceProvider";
import {navigateToCmsOrder} from "../../services/CmsService";
import {
  getDateTimeAtSpecificTimeZone,
  formatDate,
  getLocalDate,
  getLocalDateTime,
  getLocalTime,
  getTimeAtSpecificTimeZone
} from "../../utils/DateTimeUtils";
import {copyToClipboard} from "../../utils/General";

import {AuthState, useAuthState} from "../../providers/AuthProvider";
import ActiveFilterDialog, {IActiveFilterDialogProps, IActiveFilterDialogRef} from "../common/ActiveFilterDialog";
import JobNumberCellRenderer from "../common/cell-renderers/JobNumberCellRenderer";
import ColorCellRenderer from "../common/cell-renderers/ColorCellRenderer";
import doubleFieldCellRenderer, {DoubleFieldCellValue} from "../common/cell-renderers/DoubleFieldCellRenderer";
import JobStatusCellRenderer from "../common/cell-renderers/JobStatusCellRenderer";
import serviceTypeCellRenderer from "../common/cell-renderers/ServiceTypeCellRenderer";
import {TenantPreferences, UserPreferences} from "../../constants/constants";
import CustomerFilter from "../common/CustomerFilter";
import DateFilter from "../common/DateFilter";
import {useDispatchGroupContext} from "../../providers/DispatchGroupProvider";
import {useDispatchRulesDataContext} from "../../providers/DispatchRulesDataProvider";
import GridSettingsToolPanel from "../common/GridSettingsToolPanel";
import MultiSelectDropDown, {MultiSelectDropDownItem} from "../common/MultiSelectDropDown";
import Search from "../common/Search";
import {useGridRefreshOnInterval} from "../../hooks/useGridRefreshOnInterval";
import {getHighestRankedColor, NO_COLOR_FILTER_VALUE} from "../../utils/color-indicator/color-indicator.utils";
import "./styles/grids.css";

import {deliveryStop, pickupStop} from "../../utils/stops/StopUtils";
import {
  DefaultTimeFilterCustomValue,
  getTimeOptionFilterModel,
  isCustomOption,
  PlacedInLastFilterOptions,
  PlacedInLastFilterSelect,
  ReadyDueFilterOptions,
  ReadyDueTimeFilterSelect,
  TimeOption
} from "../common/time-filter-select/TimeQuickFilterSelect";
import {createJobsServerSideDatasource} from "../../services/datasources/JobServerSideDataSource.service";
import {
  AgJob,
  ColumnTypes,
  CustomRowStyle,
  addNewColumnStateAfter,
  DefaultColumnDef,
  ExtendedAgJob,
  getColorPriority,
  getWarningMessage,
  GridContext,
  LEGACY_READY_DUE_COL_IDS,
  mapReadyDueColumnState,
  SortableColumnFields,
  WARNING_MESSAGES,
  WarningText
} from "../job/JobPanel";
import {
  RoutePlanningActionType,
  useRoutePlanningContext,
  useRoutePlanningDispatch
} from "../../providers/RoutePlanningProvider";
import {
  ButtonBar,
  ButtonContainer,
  DangerButton,
  LeftControls,
  PanelContainer,
  RightControls,
  SelectedInfo
} from "./styles/styleds";

import {useAssignmentDispatch, useAssignmentState} from "../../providers/AssignmentProdiver";
import AssignmentSubscription from "../common/AssignmentSubscription";
import {UserClaims} from "@okta/okta-auth-js";
import {useOktaAuth} from "@okta/okta-react";
import {hasSelectedDispatchStations, useDispatchStationContext} from "../../providers/DispatchStationProvider";
import {CellRendererWithToolTip} from "../common/cell-renderers/CellRendererWithToolTip";
import {useDispatchDataContext} from "../../providers/DispatchDataProvider";
import BadDataOverlay from "../common/BadDataOverlay";
import {WarningIcon} from "../common/icons";
import {
  AG_GRID_NOT_EXIST_FILTER,
  getFilterValues,
  getJobRowClass,
  getOrderSkillFilterParams,
  JobGridOptions,
  orderSkillsValueGetter
} from "../../utils/ag-grid/AGGridUtils";
import {useColorColumnSortControl} from "../../hooks/useColorColumnSortControl";
import {useRedrawPendingRows} from "../../hooks/useRedrawPendingRows";
import {IP_JOB_DEFAULT_FIELDS} from "./ipJobFields";
import {ColorIndicatorCellRenderer} from "../common/cell-renderers/ColorIndicatorCellRenderer";
import {DispatchNotesProvider} from "../../providers/DispatchNotesProvider";

ModuleRegistry.registerModules([ServerSideRowModelModule, SetFilterModule, ColumnsToolPanelModule]);

// Fields the IP jobs grid fetches for every job (Step 1 selection, read again on Step 3); see ipJobFields.ts
export {IP_JOB_DEFAULT_FIELDS} from "./ipJobFields";

interface JobsGridProps {
  setGridRef: (ref: React.RefObject<AgGridReact<AgJob>>) => void;
}

const JobsGrid = ({setGridRef}: JobsGridProps) => {
  const apolloClient = useApolloClient();
  const authState = useAuthState();
  const {oktaAuth} = useOktaAuth();
  const assignmentState = useAssignmentState();
  const assignmentStateDispatch = useAssignmentDispatch();

  const {
    colorSort: filterAndSortByColorsEnabled,
    computedColumns: computedColumnsEnabled,
    skills: skillsEnabled,
    multipleSort: multipleSortEnabled,
    dispatchNote: dispatchNotesEnabled,
    backendColorRules: backendColorRulesEnabled
  } = useFeatureFlagContext();

  const {appState} = useAppContext();
  const {userPreferences, tenantPreferences} = usePreferenceContext();
  const dispatchStationState = useDispatchStationContext();
  const {selectedDispatchStations} = dispatchStationState;
  const {rules, colorRank, getConfiguredEngine} = useDispatchRulesDataContext();
  const {jobGroups} = useDispatchGroupContext();
  const dispatch = useRoutePlanningDispatch();
  const {selectedJobs} = useRoutePlanningContext();
  const {skills, services, vehicleTypes, sites, packageTypes, users, orderRequirements} = useDispatchDataContext();

  const gridRef = useRef<AgGridReact<AgJob>>(null);
  const jobDialogRefs = useRef<IActiveFilterDialogRef>(null);

  const [initializing, setInitializing] = useState<boolean>(true);
  const [gridReady, setGridReady] = useState<boolean>(false);
  const [datasourceFailed, setDatasourceFailed] = useState<ApolloError | undefined>();
  const [gridHasActiveFilters, setGridHasActiveFilters] = useState<boolean>(false);
  const [warningMessage, setWarningMessage] = useState<WARNING_MESSAGES | undefined>();

  const [datasource, setDatasource] = useState<IServerSideDatasource>();

  const [selectedJobGroups, setSelectedJobGroups] = useState<string[]>([]);
  const [selectedReadyDueFilterOption, setSelectedReadyDueFilterOption] = useState<TimeOption>();
  const [selectedPlacedInLastFilterOption, setSelectedPlacedInLastFilterOption] = useState<TimeOption>();
  const [gridContext, setGridContext] = useState<GridContext>({
    globalQuery: ""
  });

  const [_renderTrigger, setRenderTrigger] = useState({});

  // Keep refreshing while jobs are selected: rows keep their selection (getRowId) and new jobs, such as
  // ones created by a file import, show up without the dispatcher clearing the selection
  useGridRefreshOnInterval(
    "Unassigned Jobs on Route Planning",
    gridRef,
    gridReady,
    datasourceFailed,
    undefined,
    undefined,
    true
  );
  useRedrawPendingRows(gridRef, assignmentState.pendingActions, gridReady && !initializing);

  const sortingColumns =
    gridRef.current?.columnApi?.getAllGridColumns()?.filter((col) => {
      return col.getSort();
    }) ?? [];

  useColorColumnSortControl({
    gridRef,
    gridReady,
    isInitialized: !initializing,
    multipleSortEnabled,
    sortingColumns,
    sortableColumnFields: SortableColumnFields
  });

  const [getDistinctValues] = useGetDistinctValuesLazyQuery();

  const datasourceFailCallback = useCallback((error: ApolloError | undefined) => {
    setDatasourceFailed(error);
  }, []);

  const timezoneOfJobStop = extractSimplePref(tenantPreferences, TenantPreferences.timezoneOfJobStop, false).value;

  const jobGroupOptions = useMemo(() => {
    const map: Map<number, MultiSelectDropDownItem> = new Map();
    const sortedGroups = [...jobGroups].sort((a, b) => (a.dispatchGroup ?? "").localeCompare(b.dispatchGroup ?? ""));
    for (const group of sortedGroups) {
      map.set(group.dispatchGroupId!, {
        name: group.dispatchGroup,
        selected: selectedJobGroups.includes(group.dispatchGroup!),
        id: group.dispatchGroupId
      } as MultiSelectDropDownItem);
    }
    return map;
  }, [jobGroups, selectedJobGroups]);

  const refreshDataByFilterChanged = useCallback(
    (eventName: string) => {
      if (gridRef.current) {
        log.debug(`Refreshing data for event: ${eventName}`);
        gridRef.current.api.onFilterChanged();
      }
    },
    [gridRef]
  );

  const refreshDataServerSide = useCallback((eventName: string) => {
    if (gridRef.current) {
      log.debug("Refreshing server-side data: ", eventName);
      gridRef.current.api.refreshServerSide();
    }
  }, []);

  const getFilterInstance = useCallback((colId: string) => {
    if (!gridRef.current?.api) {
      return Promise.resolve(null);
    }
    return new Promise<ISetFilter | null>((resolve) => {
      gridRef.current!.api.getFilterInstance(colId, (filter) => {
        resolve(filter as ISetFilter);
      });
    });
  }, []);

  const handleGridReady = useCallback(() => {
    setGridReady(true);
    if (gridRef.current) {
      setGridRef(gridRef);
    }
  }, [setGridRef]);

  const handleConfirmReadyDueFilter = useCallback(
    (option: TimeOption) => {
      setSelectedReadyDueFilterOption(option);
      Promise.all([
        getFilterInstance("stop.delivery.scheduledDateTime"),
        getFilterInstance("stop.pickup.scheduledDateTime")
      ]).then(([deliveryFilter, pickupFilter]) => {
        if (option.type === "ready") {
          deliveryFilter?.setModel(null);
          if (pickupFilter) pickupFilter.setModel(getTimeOptionFilterModel(option));
        } else {
          pickupFilter?.setModel(null);
          if (deliveryFilter) deliveryFilter.setModel(getTimeOptionFilterModel(option));
        }
        refreshDataByFilterChanged("handleConfirmReadyDueFilter");
      });
    },
    [getFilterInstance, refreshDataByFilterChanged]
  );

  const handlePlacedInLastFilterSelected = useCallback(
    (option: TimeOption) => {
      setSelectedPlacedInLastFilterOption(option);
      getFilterInstance("order.createdDateTime").then((filter) => {
        filter?.setModel(getTimeOptionFilterModel(option));
        refreshDataByFilterChanged("handlePlacedInLastFilterSelected");
      });
    },
    [getFilterInstance, refreshDataByFilterChanged]
  );

  const handleFilterChanged = useCallback((event: FilterChangedEvent<Job>) => {
    setGridHasActiveFilters(Object.keys(event.api.getFilterModel()).length > 0);
    if (event.columns[0]?.getColId() === "dispatchGroup.name") {
      const filterModel = event.api.getFilterModel();
      const jobGroupFilter = filterModel["dispatchGroup.name"] as SetFilterModel | undefined;
      if (jobGroupFilter) {
        const selectedValues = jobGroupFilter.values;
        if (selectedValues && selectedValues.length > 0) {
          setSelectedJobGroups(selectedValues as string[]);
        } else {
          // If unselected all job groups, set "" to ensure no results are shown
          setSelectedJobGroups([""]);
        }
      } else {
        setSelectedJobGroups([]);
      }
    }
  }, []);

  const onColumnSortChanged = useCallback((e: SortChangedEvent<Job>) => {
    if (e.source === "api") return;
    setRenderTrigger({});
  }, []);

  const syncModelFilter = useCallback(
    (filterName: string, filterModel: any) => {
      getFilterInstance(filterName)
        .then((filter) => {
          if (!isEqual(filter?.getModel(), filterModel)) {
            filter?.setModel(filterModel);
          }
        })
        .finally(() => {
          refreshDataByFilterChanged(filterName);
        });
    },
    [getFilterInstance, refreshDataByFilterChanged]
  );

  const handleJobGroupSelected = (key: number) => {
    const name = jobGroupOptions.get(key)?.name;
    if (name && !selectedJobGroups.includes(name)) {
      setSelectedJobGroups([...selectedJobGroups, name]);
    }
  };

  const handleJobGroupUnselected = (key: number) => {
    const name = jobGroupOptions.get(key)?.name;
    if (name && selectedJobGroups.includes(name)) {
      const newJobGroups = selectedJobGroups.filter((n) => n !== name);
      setSelectedJobGroups(newJobGroups);
    }
  };

  const handleJobGroupUnselectedAll = useCallback(() => {
    setSelectedJobGroups([]);
  }, []);

  const handleRestoreDefaults = useCallback(
    (justRestoreFilters?: boolean) => {
      gridRef.current?.api.setFilterModel(null);
      setSelectedReadyDueFilterOption(undefined);
      setSelectedPlacedInLastFilterOption(undefined);
      setSelectedJobGroups([]);

      if (!justRestoreFilters) {
        gridRef.current?.columnApi.resetColumnState();
      }

      refreshDataByFilterChanged("onRestoreDefaults");
    },
    [refreshDataByFilterChanged]
  );

  const handleCellClicked = useCallback(() => {
    if (gridRef.current?.api.isToolPanelShowing()) {
      gridRef.current?.api.closeToolPanel();
    }
  }, []);

  const onPaginationChanged = useCallback((event: PaginationChangedEvent<Job>) => {
    const startRow = event.api.paginationGetCurrentPage() * event.api.paginationGetPageSize();
    const firstRowOnPage = event.api.getDisplayedRowAtIndex(startRow);
    if (firstRowOnPage?.failedLoad) {
      event.api.showNoRowsOverlay();
    } else if (firstRowOnPage?.data) {
      event.api.hideOverlay();
    }
  }, []);

  const onSelectionChanged = useCallback(
    (event: SelectionChangedEvent<Job>) => {
      const pendingJobIds = assignmentState.pendingActions.flatMap((pa) => pa.jobIds);
      const selectedNodes = event.api.getSelectedNodes().filter((node) => !pendingJobIds.includes(node.data!.jobId));
      const loadedJobIds: number[] = [];
      event.api.forEachNode((node) => node.data && loadedJobIds.push(node.data.jobId));
      // Selected jobs the grid hasn't loaded (e.g. just imported, or on another page) stay selected
      dispatch({
        type: RoutePlanningActionType.SYNC_GRID_SELECTED_JOBS,
        payload: {loadedJobIds, selectedJobs: selectedNodes.map((node) => node.data!)}
      });
    },
    [assignmentState.pendingActions, dispatch]
  );

  // Selected jobs that arrive in the grid later (e.g. imported jobs once replicated) get the selected style
  const selectedJobIdsRef = useRef<Set<number>>(new Set());
  selectedJobIdsRef.current = new Set(selectedJobs.map((job) => job.jobId));
  const selectLoadedSelectedJobs = useCallback(() => {
    gridRef.current?.api?.forEachNode((node) => {
      if (node.data && selectedJobIdsRef.current.has(node.data.jobId) && !node.isSelected()) {
        node.setSelected(true);
      }
    });
  }, []);
  useEffect(() => {
    if (gridReady) selectLoadedSelectedJobs();
  }, [gridReady, selectedJobs, selectLoadedSelectedJobs]);

  const handleClearAllSelectedJobs = useCallback(() => {
    if (gridRef.current) {
      gridRef.current.api.deselectAll();
    }
    // deselectAll only covers loaded rows; clear the selected jobs the grid hasn't loaded too
    dispatch({type: RoutePlanningActionType.SET_SELECTED_JOBS, payload: []});
  }, [dispatch]);

  const handleToogleToolPanel = (key: string) => {
    if (gridReady) {
      if (gridRef.current!.api.getOpenedToolPanel() === key) {
        gridRef.current!.api.closeToolPanel();
      } else {
        gridRef.current!.api.openToolPanel(key);
      }
    }
  };

  const handleColumnVisibilityChanged = useCallback(() => {
    refreshDataServerSide("onColumnVisibilityChanged");
  }, [refreshDataServerSide]);

  const handleClearReadyDueFilter = useCallback(() => {
    if (selectedReadyDueFilterOption) {
      setSelectedReadyDueFilterOption(undefined);
      getFilterInstance("stop.pickup.scheduledDateTime").then((filter) => {
        filter?.setModel(null);
      });
      getFilterInstance("stop.delivery.scheduledDateTime").then((filter) => {
        filter?.setModel(null);
      });
    }
  }, [getFilterInstance, selectedReadyDueFilterOption]);

  const handleClearPlacedInLastTime = useCallback(() => {
    if (selectedPlacedInLastFilterOption) {
      setSelectedPlacedInLastFilterOption(undefined);
      getFilterInstance("order.createdDateTime").then((filter) => {
        filter?.setModel(null);
      });
    }
  }, [getFilterInstance, selectedPlacedInLastFilterOption]);

  const handleGlobalSearch = useCallback(
    (query: string) => {
      if (gridContext.globalQuery != query) {
        setGridContext({...gridContext, globalQuery: query});
      }
    },
    [gridContext]
  );

  const getColorIndicator = useCallback(
    async (job: AgJob) => {
      const colors: string[] = [];

      for (const stop of job.stops) {
        const engine = getConfiguredEngine({job: job, stop: stop}, rules as RuleProperties[]);
        if (engine === undefined) return;

        const results = await engine.run();

        if (results.events.length > 0) {
          colors.push(results.events[0].params?.color);
        }
      }

      job.indicatorColor = colorRank ? getHighestRankedColor(colors, colorRank) : undefined;
    },
    [colorRank, getConfiguredEngine, rules]
  );

  const buildIndicatorColors = useCallback(
    async (params: IServerSideGetRowsParams, newRows: AgJob[]) => {
      const promises = newRows.map(async (incomingRow) => {
        await getColorIndicator(incomingRow);
      });

      const results = await Promise.allSettled(promises);

      for (const result of results) {
        if (result.status === "rejected") {
          log.warn("Error getting color indicator", result.reason);
        }
      }
    },
    [getColorIndicator]
  );

  const sortAndFilterByColors = useCallback(
    async (params: IServerSideGetRowsParams, newRows: AgJob[], res: ApolloQueryResult<any>) => {
      if (filterAndSortByColorsEnabled) {
        const {sortModel, filterModel} = params.request;
        const filterByColors = filterModel?.indicatorColor;
        const sortByColors = sortModel.find((item) => item.colId === "indicatorColor");
        if (filterByColors) {
          filterByColors.values[filterByColors.values.indexOf("No Color")] = undefined;
          for (let i = 0; i < newRows.length; i++) {
            if (!filterByColors.values.includes(newRows[i].indicatorColor)) {
              (newRows[i] as ExtendedAgJob).hide = true;
            }
          }
        }
        if (sortByColors) {
          const sortDirection = sortByColors.sort === "asc" ? 1 : -1;
          newRows.sort((a, b) => {
            return (
              (getColorPriority(rules, a.indicatorColor) - getColorPriority(rules, b.indicatorColor)) * sortDirection
            );
          });
        }
        const hideRows = newRows.filter((row) => (row as ExtendedAgJob).hide);

        for (const hideRow of hideRows) {
          const hideRowIndex = newRows.findIndex((row) => row.jobId === hideRow.jobId);
          newRows.splice(hideRowIndex, 1);
          newRows.push(hideRow);
        }

        if (res.data.searchJobs.total > 100) {
          const warningMsg = getWarningMessage(Boolean(sortByColors), Boolean(filterByColors));
          setWarningMessage(warningMsg);
        } else {
          setWarningMessage(undefined);
        }
      }
    },
    [filterAndSortByColorsEnabled, rules]
  );

  const colorColDefs: ColDef[] = useMemo(() => {
    return filterAndSortByColorsEnabled
      ? ([
          {
            field: "indicatorColor",
            headerName: "Color",
            headerClass: "ag-header-color-column",
            cellClass: "ag-cell-color-column",
            pinned: "left",
            lockPosition: "left",
            lockPinned: true,
            suppressMovable: true,
            suppressColumnsToolPanel: true,
            sortable: true,
            resizable: false,
            width: 60,
            cellRenderer: ColorIndicatorCellRenderer,
            type: "filterColumn",
            filter: "agSetColumnFilter",
            filterParams: {
              cellRenderer: ColorCellRenderer,
              values: [NO_COLOR_FILTER_VALUE, ...(rules ?? []).map((rule) => rule.event.params?.color)],
              suppressMiniFilter: true
            }
          }
        ] as ColDef[])
      : ([
          {
            field: "indicatorColor",
            headerName: "Color",
            pinned: "left",
            lockPosition: "left",
            lockPinned: true,
            suppressMovable: true,
            sortable: false,
            rowDrag: false,
            resizable: false,
            width: 18,
            minWidth: 18,
            cellRenderer: ColorIndicatorCellRenderer,
            headerComponent: () => ""
          }
        ] as ColDef[]);
  }, [filterAndSortByColorsEnabled, rules]);

  const skillsColumnDef: ColDef[] = useMemo(
    () =>
      skillsEnabled
        ? [
            {
              colId: "order.orderSkills.id",
              field: "order.orderSkills.id",
              headerName: "Skills",
              sortable: false,
              initialHide: true,
              type: "filterColumn",
              filter: "agSetColumnFilter",
              filterParams: getOrderSkillFilterParams(skills),
              valueGetter: (params: any) => {
                return orderSkillsValueGetter(params, skills);
              },
              cellRenderer: CellRendererWithToolTip
            } as ColDef
          ]
        : [],
    [skills, skillsEnabled]
  );

  const columnDefs: ColDef[] = useMemo(
    () => {
      const computedColumns = computedColumnsEnabled
        ? [
            {
              colId: "readyAt",
              field: "readyAt",
              headerName: "Ready At",
              type: "filterColumn",
              sortable: true,
              filter: DateFilter,
              filterParams: {type: "range"},
              cellRenderer: doubleFieldCellRenderer,
              valueGetter: (params: any) => {
                const timeZone = pickupStop(params.data.stops)?.timeZone;
                const getTimeFnc = timezoneOfJobStop ? getTimeAtSpecificTimeZone : getLocalTime;
                const getDateFnc = timezoneOfJobStop ? formatDate : getLocalDate;

                return {
                  upperField: {
                    value: getTimeFnc({
                      dateStr: params.data.readyAt,
                      timeZone,
                      isTimeFormat24hr: appState.isTimeFormat24hr,
                      options: {
                        includeZone: true
                      }
                    })
                  },
                  lowerField: {
                    value: getDateFnc(params.data.readyAt, timeZone, "EEE MMM dd yyyy")
                  }
                } as DoubleFieldCellValue;
              }
            },
            {
              colId: "dueBy",
              field: "dueBy",
              headerName: "Due By",
              type: "filterColumn",
              sortable: true,
              filter: DateFilter,
              filterParams: {type: "range"},
              cellRenderer: doubleFieldCellRenderer,
              valueGetter: (params: any) => {
                const timeZone = deliveryStop(params.data.stops)?.timeZone;
                const getTimeFnc = timezoneOfJobStop ? getTimeAtSpecificTimeZone : getLocalTime;
                const getDateFnc = timezoneOfJobStop ? formatDate : getLocalDate;

                return {
                  upperField: {
                    value: getTimeFnc({
                      dateStr: params.data.dueBy,
                      timeZone,
                      isTimeFormat24hr: appState.isTimeFormat24hr,
                      options: {includeZone: true}
                    })
                  },
                  lowerField: {
                    value: getDateFnc(params.data.dueBy, timeZone, "EEE MMM dd yyyy")
                  }
                } as DoubleFieldCellValue;
              }
            },
            {
              colId: "stop.pickup.scheduledDateTime",
              field: "stops.scheduledDateTime,stops.timeZone,stops.stopType",
              sortable: true,
              headerName: "Ready_Hidden",
              suppressColumnsToolPanel: true,
              type: "filterColumn",
              hide: true,
              filter: DateFilter,
              filterParams: {type: "range"},
              cellRenderer: doubleFieldCellRenderer,
              valueGetter: (params: any) => {
                const scheduledDateTime = pickupStop(params.data.stops)?.scheduledDateTime;
                const timeZone = pickupStop(params.data.stops)?.timeZone;
                const getTimeFnc = timezoneOfJobStop ? getTimeAtSpecificTimeZone : getLocalTime;
                const getDateFnc = timezoneOfJobStop ? formatDate : getLocalDate;

                return {
                  upperField: {
                    value: getTimeFnc({
                      dateStr: scheduledDateTime,
                      timeZone,
                      isTimeFormat24hr: appState.isTimeFormat24hr,
                      options: {
                        includeZone: true
                      }
                    })
                  },
                  lowerField: {
                    value: getDateFnc(scheduledDateTime, timeZone, "EEE MMM dd yyyy")
                  }
                } as DoubleFieldCellValue;
              }
            },
            {
              colId: "stop.delivery.scheduledDateTime",
              field: "stops.scheduledDateTime,stops.timeZone,stops.stopType",
              sortable: true,
              headerName: "Due By_Hidden",
              suppressColumnsToolPanel: true,
              type: "filterColumn",
              hide: true,
              filter: DateFilter,
              filterParams: {type: "range"},
              cellRenderer: doubleFieldCellRenderer,
              valueGetter: (params: any) => {
                const scheduledDateTime = deliveryStop(params.data.stops)?.scheduledDateTime;
                const timeZone = deliveryStop(params.data.stops)?.timeZone;
                const getTimeFnc = timezoneOfJobStop ? getTimeAtSpecificTimeZone : getLocalTime;
                const getDateFnc = timezoneOfJobStop ? formatDate : getLocalDate;
                return {
                  upperField: {
                    value: getTimeFnc({
                      dateStr: scheduledDateTime,
                      timeZone,
                      isTimeFormat24hr: appState.isTimeFormat24hr,
                      options: {includeZone: true}
                    })
                  },
                  lowerField: {
                    value: getDateFnc(scheduledDateTime, timeZone, "EEE MMM dd yyyy")
                  }
                } as DoubleFieldCellValue;
              }
            }
          ]
        : [
            {
              colId: "stop.pickup.scheduledDateTime",
              field: "stops.scheduledDateTime,stops.timeZone,stops.stopType",
              sortable: true,
              headerName: "Ready",
              type: "filterColumn",
              filter: DateFilter,
              filterParams: {type: "range"},
              cellRenderer: doubleFieldCellRenderer,
              valueGetter: (params: any) => {
                const scheduledDateTime = pickupStop(params.data.stops)?.scheduledDateTime;
                const timeZone = pickupStop(params.data.stops)?.timeZone;
                const getTimeFnc = timezoneOfJobStop ? getTimeAtSpecificTimeZone : getLocalTime;
                const getDateFnc = timezoneOfJobStop ? formatDate : getLocalDate;

                return {
                  upperField: {
                    value: getTimeFnc({
                      dateStr: scheduledDateTime,
                      timeZone,
                      isTimeFormat24hr: appState.isTimeFormat24hr,
                      options: {
                        includeZone: true
                      }
                    })
                  },
                  lowerField: {
                    value: getDateFnc(scheduledDateTime, timeZone, "EEE MMM dd yyyy")
                  }
                } as DoubleFieldCellValue;
              }
            },
            {
              colId: "stop.delivery.scheduledDateTime",
              field: "stops.scheduledDateTime,stops.timeZone,stops.stopType",
              sortable: true,
              headerName: "Due By",
              type: "filterColumn",
              filter: DateFilter,
              filterParams: {type: "range"},
              cellRenderer: doubleFieldCellRenderer,
              valueGetter: (params: any) => {
                const scheduledDateTime = deliveryStop(params.data.stops)?.scheduledDateTime;
                const timeZone = deliveryStop(params.data.stops)?.timeZone;
                const getTimeFnc = timezoneOfJobStop ? getTimeAtSpecificTimeZone : getLocalTime;
                const getDateFnc = timezoneOfJobStop ? formatDate : getLocalDate;

                return {
                  upperField: {
                    value: getTimeFnc({
                      dateStr: scheduledDateTime,
                      timeZone,
                      isTimeFormat24hr: appState.isTimeFormat24hr,
                      options: {includeZone: true}
                    })
                  },
                  lowerField: {
                    value: getDateFnc(scheduledDateTime, timeZone, "EEE MMM dd yyyy")
                  }
                } as DoubleFieldCellValue;
              }
            }
          ];

      return [
        ...colorColDefs,
        {
          field: "jobNumber",
          headerName: "Job #",
          pinned: "left",
          lockPosition: "left",
          sortable: true,
          type: ["filterColumn", "textFilter"],
          rowDrag: false,
          cellClass: "job-number-cell",
          minWidth: 50,
          cellRenderer: JobNumberCellRenderer,
          valueGetter: (params) => {
            return {
              testIdPrefix: "more-details-button",
              text: params.data.jobNumber,
              minimal: true,
              intent: Intent.PRIMARY,
              onClick: (event: React.MouseEvent<HTMLElement, MouseEvent>, data: any, authState: AuthState) => {
                gridRef.current?.api.showLoadingOverlay();
                navigateToCmsOrder(
                  authState.tenant!,
                  oktaAuth.getAccessToken() ?? "",
                  data.order.orderId,
                  tenantPreferences
                ).finally(() => gridRef.current?.api.hideOverlay());
              },
              dispatchNotesEnabled: dispatchNotesEnabled
            };
          }
        },
        ...computedColumns,
        {
          colId: "order.customer.name",
          field: "order.customer.name",
          headerName: "Customer Account",
          type: "filterColumn",
          sortable: true,
          filter: CustomerFilter
        },
        {
          colId: "stop.pickup.dispatchZone",
          field: "stops.dispatchZone,stops.stopType",
          headerName: "Pickup Zone",
          sortable: true,
          valueGetter: (params) => pickupStop(params.data.stops)?.dispatchZone,
          type: ["filterColumn", "textFilter"]
        },
        {
          colId: "stop.delivery.dispatchZone",
          field: "stops.dispatchZone,stops.stopType",
          headerName: "Delivery Zone",

          sortable: true,
          valueGetter: (params) => deliveryStop(params.data.stops)?.dispatchZone,
          type: ["filterColumn", "textFilter"]
        },
        {
          field: "service",
          headerName: "SVC Type",
          cellRenderer: serviceTypeCellRenderer,
          sortable: true,
          type: "filterColumn",
          filter: "agSetColumnFilter",
          filterParams: {
            cellRenderer: serviceTypeCellRenderer,
            values: services
          }
        },
        {
          field: "vehicleType.description",
          headerName: "Vehicle",
          sortable: true,
          type: "filterColumn",
          filter: "agSetColumnFilter",
          filterParams: {
            values: vehicleTypes
          }
        },
        {
          colId: "stop.pickup.address",
          field: "stops.address,stops.stopType",
          headerName: "Pickup Address",
          sortable: true,
          valueGetter: (params) => pickupStop(params.data.stops)?.address,
          type: ["filterColumn", "textFilter"],
          cellStyle: {
            cursor: "pointer"
          },
          cellClass: "unassigned-jobs-pickup-address",
          onCellClicked: (params: CellClickedEvent<Job>) => {
            if (!params.data) return;
            const address = pickupStop(params.data.stops)?.address ?? "";
            copyToClipboard(address).then(() => {
              /*Noop*/
            });
          }
        },
        {
          colId: "stop.delivery.address",
          field: "stops.address,stops.stopType",
          headerName: "Delivery Address",
          valueGetter: (params) => deliveryStop(params.data.stops)?.address,
          type: ["filterColumn", "textFilter"],
          cellStyle: {
            cursor: "pointer"
          },
          cellClass: "unassigned-jobs-delivery-address",
          onCellClicked: (params: CellClickedEvent<Job>) => {
            if (!params.data) return;
            const address = deliveryStop(params.data.stops)?.address ?? "";
            copyToClipboard(address).then(() => {
              /*Noop*/
            });
          }
        },
        {field: "pieces", headerName: "Pieces", sortable: true, type: ["filterColumn", "numberFilter"], maxWidth: 67},
        {field: "weight", headerName: "Weight", sortable: true, type: ["filterColumn", "numberFilter"], maxWidth: 70},
        {field: "order.notes", headerName: "Order Notes", type: ["filterColumn", "textFilter"]},

        //------------DESELECT FIELDS------------//
        {
          field: "stops.dispatchZone",
          headerName: "Zone",
          cellRenderer: doubleFieldCellRenderer,
          hide: true,
          valueGetter: (params) => {
            return {
              upperField: {
                value: pickupStop(params.data.stops)?.dispatchZone,
                tooltip: `Pickup: ${pickupStop(params.data.stops)?.dispatchZone}`
              },
              lowerField: {
                value: deliveryStop(params.data.stops)?.dispatchZone,
                tooltip: `Delivery: ${deliveryStop(params.data.stops)?.dispatchZone}`,
                style: {fontWeight: "lighter"}
              }
            } as DoubleFieldCellValue;
          }
        },
        {
          field: "time",
          headerName: "Time",
          cellRenderer: doubleFieldCellRenderer,
          hide: true,
          valueGetter: (params) => {
            const pickupScheduledDateTime = pickupStop(params.data.stops)?.scheduledDateTime;
            const pickupTimeZone = pickupStop(params.data.stops)?.timeZone;
            const deliveryScheduledDateTime = deliveryStop(params.data.stops)?.scheduledDateTime;
            const deliveryTimeZone = deliveryStop(params.data.stops)?.timeZone;

            const getTimeFnc = timezoneOfJobStop ? getTimeAtSpecificTimeZone : getLocalTime;
            const getDateFnc = timezoneOfJobStop ? getDateTimeAtSpecificTimeZone : getLocalDateTime;

            return {
              upperField: {
                value: getTimeFnc({
                  dateStr: pickupScheduledDateTime,
                  timeZone: pickupTimeZone,
                  isTimeFormat24hr: appState.isTimeFormat24hr,
                  options: {
                    includeZone: true
                  }
                }),
                tooltip: `Pickup: ${getDateFnc({
                  dateStr: pickupStop(params.data.stops)?.scheduledDateTime,
                  timeZone: pickupStop(params.data.stops)?.timeZone,
                  isTimeFormat24hr: appState.isTimeFormat24hr
                })}`
              },
              lowerField: {
                value: getTimeFnc({
                  dateStr: deliveryScheduledDateTime,
                  timeZone: deliveryTimeZone,
                  isTimeFormat24hr: appState.isTimeFormat24hr,
                  options: {
                    includeZone: true
                  }
                }),
                tooltip: `Delivery: ${getDateFnc({
                  dateStr: deliveryStop(params.data.stops)?.scheduledDateTime,
                  timeZone: deliveryStop(params.data.stops)?.timeZone,
                  isTimeFormat24hr: appState.isTimeFormat24hr
                })}`,
                style: {fontWeight: "lighter"}
              }
            } as DoubleFieldCellValue;
          }
        },
        {
          field: "stops.address",
          headerName: "Address",
          cellRenderer: doubleFieldCellRenderer,
          hide: true,
          valueGetter: (params) => {
            return {
              upperField: {
                value: pickupStop(params.data.stops)?.address,
                tooltip: `Pickup: ${pickupStop(params.data.stops)?.address}`
              },
              lowerField: {
                value: deliveryStop(params.data.stops)?.address,
                tooltip: `Delivery: ${deliveryStop(params.data.stops)?.address}`,
                style: {fontWeight: "lighter"}
              }
            } as DoubleFieldCellValue;
          }
        },
        {
          field: "dispatchGroup.name",
          headerName: "Job Group",
          hide: true,
          sortable: true,
          type: "filterColumn",
          filter: "agSetColumnFilter",
          filterParams: {
            values: jobGroups.map((d) => d.dispatchGroup!)
          }
        },
        {
          field: "jobStatus",
          headerName: "Status",
          sortable: true,
          hide: true,
          suppressColumnsToolPanel: true,
          cellRenderer: JobStatusCellRenderer,
          type: "filterColumn",
          filter: "agSetColumnFilter",
          filterParams: {
            cellRenderer: JobStatusCellRenderer,
            values: (params: SetFilterValuesFuncParams) => {
              getFilterValues(params, getDistinctValues, "jobs", "jobStatus");
            }
          }
        },
        {field: "order.caller", headerName: "Caller", hide: true, type: ["filterColumn", "textFilter"], sortable: true},
        {
          field: "order.callerPhone",
          headerName: "Phone",
          hide: true,
          type: ["filterColumn", "textFilter"],
          sortable: true
        },
        {
          field: "order.customer.alias",
          headerName: "Customer Alias",
          hide: true,
          type: ["filterColumn", "textFilter"],
          sortable: true
        },
        {
          field: "order.customer.customerCode",
          headerName: "Account #",
          hide: true,
          type: ["filterColumn", "textFilter"],
          sortable: true
        },
        {
          field: "order.customer.notes",
          headerName: "Customer Notes",
          hide: true,
          type: ["filterColumn", "textFilter"]
        },
        {
          field: "driver.driverCode",
          headerName: "Driver Code",
          hide: true,
          type: ["filterColumn", "textFilter"],
          sortable: true
        },
        {field: "order.liftGateRequired", headerName: "Lift Gate", hide: true /*TODO: BOOLEAN Filter*/},
        {
          field: "site.code",
          headerName: "Site",
          sortable: true,
          hide: true,
          type: ["filterColumn"],
          filter: "agSetColumnFilter",
          filterParams: {
            values: sites
          }
        },
        {
          field: "order.alias",
          headerName: "Order Alias",
          hide: true,
          type: ["filterColumn", "textFilter"],
          sortable: true
        },
        {
          field: "jobAlias",
          headerName: "Job Alias",
          hide: true,
          type: ["filterColumn", "textFilter"],
          sortable: true
        },
        {
          field: "order.auth",
          headerName: "Order Auth",
          hide: true,
          type: ["filterColumn", "textFilter"],
          sortable: true
        },
        {
          field: "order.orderId",
          headerName: "Order Id",
          hide: true,
          type: ["filterColumn", "numberFilter"],
          sortable: true
        },
        {
          field: "order.codAmount",
          headerName: "COD Amount",
          hide: true,
          type: ["filterColumn", "numberFilter"],
          sortable: true
        },
        {
          field: "order.description",
          headerName: "Order Description",
          hide: true,
          type: ["filterColumn", "textFilter"],
          sortable: true
        },
        {
          field: "order.priority.description",
          headerName: "Order Priority",
          hide: true,
          type: ["filterColumn", "textFilter"],
          sortable: true
        },
        {
          field: "routeNumber",
          headerName: "Route#",
          sortable: true,
          hide: true,
          type: ["filterColumn", "textFilter"]
        },
        {
          field: "order.totalMiles",
          headerName: "Total Mileage",
          hide: true,
          type: ["filterColumn", "numberFilter"],
          sortable: true
        },
        {
          field: "order.origin",
          headerName: "Origin",
          hide: true,
          type: ["filterColumn", "textFilter"],
          sortable: true
        },
        {
          field: "order.packageType.description",
          headerName: "Package Type",
          hide: true,
          type: ["filterColumn"],
          sortable: true,
          filter: "agSetColumnFilter",
          filterParams: {
            values: packageTypes
          }
        },
        {
          field: "order.qualityFlag",
          headerName: "Quality Flag",
          hide: true,
          sortable: true /*TODO: BOOLEAN Filter*/
        },
        {
          colId: "stop.pickup.name",
          field: "stops.name,stops.stopType",
          headerName: "Pickup Name",
          valueGetter: (params) => pickupStop(params.data.stops)?.name,
          hide: true,
          type: ["filterColumn", "textFilter"]
        },
        {
          colId: "stop.pickup.city",
          field: "stops.city,stops.stopType",
          headerName: "Pickup City",
          sortable: true,
          valueGetter: (params) => pickupStop(params.data.stops)?.city,
          hide: true,
          type: ["filterColumn", "textFilter"]
        },
        {
          colId: "stop.pickup.state",
          field: "stops.state,stops.stopType",
          headerName: "Pickup State",
          valueGetter: (params) => pickupStop(params.data.stops)?.state,
          hide: true,
          type: ["filterColumn", "textFilter"]
        },
        {
          colId: "stop.pickup.note",
          field: "stops.note,stops.stopType",
          headerName: "Pickup Notes",
          valueGetter: (params) => pickupStop(params.data.stops)?.note,
          hide: true,
          type: ["filterColumn", "textFilter"]
        },
        {
          colId: "stop.pickup.zip",
          field: "stops.zip,stops.stopType",
          headerName: "Pickup Zip Code",
          valueGetter: (params) => pickupStop(params.data.stops)?.zip,
          hide: true,
          type: ["filterColumn", "textFilter"],
          sortable: true
        },
        {
          colId: "stop.delivery.name",
          field: "stops.name,stops.stopType",
          headerName: "Delivery Name",
          valueGetter: (params) => deliveryStop(params.data.stops)?.name,
          hide: true,
          type: ["filterColumn", "textFilter"]
        },
        {
          colId: "stop.delivery.city",
          field: "stops.city,stops.stopType",
          headerName: "Delivery City",
          sortable: true,
          valueGetter: (params) => deliveryStop(params.data.stops)?.city,
          hide: true,
          type: ["filterColumn", "textFilter"]
        },
        {
          colId: "stop.delivery.state",
          field: "stops.state,stops.stopType",
          headerName: "Delivery State",
          valueGetter: (params) => deliveryStop(params.data.stops)?.state,
          hide: true,
          type: ["filterColumn", "textFilter"]
        },
        {
          colId: "stop.delivery.note",
          field: "stops.note,stops.stopType",
          headerName: "Delivery Notes",
          valueGetter: (params) => deliveryStop(params.data.stops)?.note,
          hide: true,
          type: ["filterColumn", "textFilter"]
        },
        {
          colId: "stop.delivery.zip",
          field: "stops.zip,stops.stopType",
          headerName: "Delivery Zip Code",
          valueGetter: (params) => deliveryStop(params.data.stops)?.zip,
          hide: true,
          type: ["filterColumn", "textFilter"],
          sortable: true
        },
        {
          field: "stopCount",
          headerName: "Number of Stops",
          sortable: true,
          type: ["filterColumn", "numberFilter"],
          hide: true
        },
        {
          field: "order.requirements.name",
          headerName: "Order Requirements",
          valueGetter: (params) => params.data.order?.requirements?.map((or: OrderRequirement) => or.name).join(", "),
          sortable: false,
          hide: true,
          type: ["filterColumn"],
          filter: "agSetColumnFilter",
          filterParams: {
            values: [AG_GRID_NOT_EXIST_FILTER, ...orderRequirements],
            valueFormatter: (params: any) => {
              if (params.value === AG_GRID_NOT_EXIST_FILTER) {
                return "No Requirements";
              }
              return params.value;
            }
          }
        },
        {
          field: "order.createdDateTime",
          hide: true,
          sortable: true,
          headerName: "Order Placed",
          type: "filterColumn",
          filter: DateFilter,
          filterParams: {
            type: "range",
            dateRangeShortcuts: [
              {label: "Today", dateRange: [new Date(), new Date()]},
              {label: "Yesterday", dateRange: [subDays(new Date(), 1), subDays(new Date(), 1)]},
              {label: "Last 7 days", dateRange: [subDays(new Date(), 6), new Date()]},
              {label: "Last 14 days", dateRange: [subDays(new Date(), 13), new Date()]}
            ]
          },
          cellRenderer: doubleFieldCellRenderer,
          valueGetter: (params: ValueGetterParams<any>) => {
            return {
              upperField: {
                value: getLocalTime({
                  dateStr: params.data.order.createdDateTime,
                  timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                  isTimeFormat24hr: appState.isTimeFormat24hr,
                  options: {
                    includeZone: true
                  }
                })
              },
              lowerField: {
                value: getLocalDate(
                  params.data.order.createdDateTime,
                  Intl.DateTimeFormat().resolvedOptions().timeZone,
                  "EEE MMM dd yyyy"
                )
              }
            } as DoubleFieldCellValue;
          }
        },
        {
          field: "order.declaredValue",
          headerName: "Declared Value",
          sortable: true,
          type: ["filterColumn", "numberFilter"],
          initialHide: true
        },
        {
          field: "order.user.userName",
          headerName: "User",
          initialHide: true,
          type: "filterColumn",
          filter: "agSetColumnFilter",
          filterParams: {
            values: users
          },
          sortable: true
        },
        ...skillsColumnDef
      ];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [colorColDefs, skillsColumnDef, services, vehicleTypes, packageTypes, users, dispatchNotesEnabled]
  ) as ColDef[];

  const sideBar = useMemo<SideBarDef | string | string[] | boolean | null>(() => {
    return {
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          toolPanelParams: {
            suppressColumnSelectAll: true,
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true
          }
        },
        {
          id: "settings",
          labelDefault: "Settings",
          labelKey: "settings",
          iconKey: "menu",
          toolPanel: GridSettingsToolPanel,
          toolPanelParams: {
            onRestoreDefaults: handleRestoreDefaults
          }
        }
      ],
      defaultToolPanel: "customStats"
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeFilterProps: IActiveFilterDialogProps = useMemo(
    () => ({
      message:
        "To view all unassigned jobs, please clear any active filters in the table header that may be limiting your job list.",
      title: "Active Filters Present",
      icon: <Icon icon="filter" color="green" size={21} />,
      actionButton: {
        label: "Remove Filter(s)",
        action: handleRestoreDefaults.bind(null, true)
      }
    }),
    [handleRestoreDefaults]
  );

  // Initialize the grid and apply saved preferences
  useEffect(() => {
    if (initializing && gridReady) {
      let savedColumnState = extractJsonPref(userPreferences, UserPreferences.jobGridColumnState).value;
      if (savedColumnState) {
        if (computedColumnsEnabled) {
          savedColumnState = mapReadyDueColumnState(savedColumnState);
        }
        savedColumnState = addNewColumnStateAfter(savedColumnState, "jobAlias", "order.alias");
        gridRef.current!.columnApi.applyColumnState({state: savedColumnState, applyOrder: true});
      }

      const savedFilterState = extractJsonPref(userPreferences, UserPreferences.jobGridFilterState).value;
      const hasFilterState = savedFilterState && Object.keys(savedFilterState).length > 0;
      if (hasFilterState) {
        gridRef.current!.api.setFilterModel(savedFilterState);
        if (Object.keys(savedFilterState).includes("dispatchGroup.name")) {
          // Ensure the saved values are valid in driverGroupOptions
          const validGroupNames = new Set(Array.from(jobGroupOptions.values()).map((item) => item.name));
          const filteredValues = savedFilterState["dispatchGroup.name"].values.filter((name: string) =>
            validGroupNames.has(name)
          );
          setSelectedJobGroups(filteredValues);
        }
      }

      const savedReadyDueFilterCustomValue = extractJsonPref(
        userPreferences,
        UserPreferences.jobGridFilterReadyDueCustomValue,
        DefaultTimeFilterCustomValue
      ).value;
      if (savedReadyDueFilterCustomValue) {
        for (const key of Object.keys(savedReadyDueFilterCustomValue)) {
          const customOptionIndex = ReadyDueFilterOptions.findIndex((o) => isCustomOption(o) && o.type === key);
          if (customOptionIndex > -1) {
            ReadyDueFilterOptions[customOptionIndex].customValue = savedReadyDueFilterCustomValue[key];
          }
        }
      }

      const savedReadyDueFilter = extractJsonPref(userPreferences, UserPreferences.jobGridFilterReadyDue).value;
      if (savedReadyDueFilter) {
        const title = savedReadyDueFilter.title;
        const type = savedReadyDueFilter.type;
        const option = ReadyDueFilterOptions.find((o) => o.title === title && o.type === type);
        if (option) {
          handleConfirmReadyDueFilter(option);
        }
      }

      const savedPlacedInLastFilter = extractJsonPref(userPreferences, UserPreferences.jobGridFilterPlaced).value;
      if (savedPlacedInLastFilter) {
        const option = PlacedInLastFilterOptions.find((o) => o.title === savedPlacedInLastFilter);
        if (option) {
          handlePlacedInLastFilterSelected(option);
        }
      }

      const hasFilterJob = hasFilterState | savedReadyDueFilter | savedPlacedInLastFilter;
      if (hasFilterJob) {
        jobDialogRefs.current?.onOpen();
      }
      setInitializing(false);
    }
  }, [
    computedColumnsEnabled,
    gridReady,
    handlePlacedInLastFilterSelected,
    initializing,
    handleConfirmReadyDueFilter,
    userPreferences,
    jobGroupOptions
  ]);

  // Re-assert the hidden invariant when computedColumns resolves after grid init (Root Cause A fix)
  useEffect(() => {
    if (!gridReady || !computedColumnsEnabled) return;
    gridRef.current?.columnApi.applyColumnState({
      state: LEGACY_READY_DUE_COL_IDS.map((colId) => ({colId, hide: true}))
    });
  }, [gridReady, computedColumnsEnabled]);

  // Create the server-side datasource when the grid is ready
  useEffect(() => {
    if (gridReady) {
      setDatasource(
        createJobsServerSideDatasource({
          client: apolloClient,
          defaultFields: IP_JOB_DEFAULT_FIELDS,
          excludeFields: ["map", ...(backendColorRulesEnabled ? [] : ["indicatorColor"]), "time"],
          onDatasourceFail: datasourceFailCallback,
          decorateResults: [...(backendColorRulesEnabled ? [] : [buildIndicatorColors, sortAndFilterByColors])],
          tokenExpiry: authState.expiresAt ? new Date(authState.expiresAt) : undefined,
          backendColorRules: backendColorRulesEnabled
        })
      );
    }
  }, [
    apolloClient,
    buildIndicatorColors,
    datasourceFailCallback,
    sortAndFilterByColors,
    authState.expiresAt,
    gridReady
  ]);

  //if no job groups are selected, but has selected dispatch stations, set the global filter to only include jobs  of those dispatch stations
  useEffect(() => {
    setGridContext({
      ...gridContext,
      globalFilter: hasSelectedDispatchStations(dispatchStationState)
        ? {
            or: Array.from(jobGroupOptions.values()).map((jg) => {
              return {dispatchGroup_dispatchGroupId: {eq: jg.id}} as JobAttributeFilter;
            })
          }
        : undefined
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobGroupOptions, dispatchStationState]);

  // Set filter values for dispatchGroup.dispatchGroupId when jobGroupOptions changes
  useEffect(() => {
    if (gridReady && jobGroupOptions.size > 0) {
      getFilterInstance("dispatchGroup.name").then((filter) => {
        const values = Array.from(jobGroupOptions.values()).map((item) => item.name.toString());
        filter?.setFilterValues(values);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobGroupOptions, gridReady, getFilterInstance]);

  // Reset the selected job group names when selectedDispatchStations changes
  useEffect(
    () => {
      setSelectedJobGroups([]);
      syncModelFilter("dispatchGroup.name", null);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedDispatchStations.size]
  );

  // Refresh data when jobGroupOptions changes
  useEffect(() => {
    if (gridReady) {
      refreshDataByFilterChanged("jobGroupOptions");
    }
  }, [jobGroupOptions, gridReady, refreshDataByFilterChanged]);

  // Refresh data and Update the job groups filter model when selectedJobGroups changed
  useEffect(() => {
    if (gridReady && !initializing) {
      syncModelFilter(
        "dispatchGroup.name",
        selectedJobGroups.length > 0 ? {type: "set", values: selectedJobGroups} : null
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJobGroups]);

  // Refresh data when gridContext changed
  useEffect(() => {
    if (gridReady) {
      refreshDataByFilterChanged("gridContext");
    }
  }, [gridContext, gridReady, refreshDataByFilterChanged]);

  return (
    <DispatchNotesProvider>
      <PanelContainer className="ag-theme-alpine">
        <AssignmentSubscription dispatch={assignmentStateDispatch} userClaims={authState.user as UserClaims} />
        <ActiveFilterDialog ref={jobDialogRefs} {...activeFilterProps} />
        <ButtonBar hasWarningMessage={Boolean(warningMessage)}>
          <ButtonContainer>
            <LeftControls>
              <MultiSelectDropDown
                testIdPrefix={"job-group-multiselect"}
                text={"Groups"}
                items={jobGroupOptions}
                onSelected={handleJobGroupSelected}
                onUnselected={handleJobGroupUnselected}
                onUnselectedAll={handleJobGroupUnselectedAll}
                clearFilter
              />
              <ReadyDueTimeFilterSelect
                selectedOption={selectedReadyDueFilterOption}
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                onOptionSelected={() => {}}
                onConfirm={handleConfirmReadyDueFilter}
                onClear={handleClearReadyDueFilter}
              />
              <PlacedInLastFilterSelect
                selectedOption={selectedPlacedInLastFilterOption}
                onOptionSelected={handlePlacedInLastFilterSelected}
                onClear={handleClearPlacedInLastTime}
              />
            </LeftControls>
            <RightControls>
              <ControlGroup>
                <Search
                  idPrefix={"jobs"}
                  onSearch={handleGlobalSearch}
                  isConstrainedByFilters={gridHasActiveFilters}
                  minimal
                />
                <Button
                  variant="minimal"
                  title={"Columns"}
                  icon={<FontAwesomeIcon icon={solid("line-columns")} />}
                  onClick={() => {
                    handleToogleToolPanel("columns");
                  }}
                />
                <Button
                  variant="minimal"
                  title={"Settings"}
                  icon={<FontAwesomeIcon icon={solid("sliders")} />}
                  onClick={() => {
                    handleToogleToolPanel("settings");
                  }}
                />
              </ControlGroup>
            </RightControls>
          </ButtonContainer>
          {warningMessage && (
            <WarningText>
              <WarningIcon />
              <Text ellipsize>{warningMessage}</Text>
            </WarningText>
          )}
        </ButtonBar>
        <AgGridReact<Job>
          className={"jobs-grid"}
          ref={gridRef}
          getRowClass={(params) => getJobRowClass(params, assignmentState.pendingActions)}
          suppressAggAtRootLevel
          onGridReady={handleGridReady}
          columnDefs={columnDefs}
          context={gridContext}
          suppressCellFocus
          defaultColDef={DefaultColumnDef}
          columnTypes={ColumnTypes}
          rowSelection={"multiple"}
          rowMultiSelectWithClick
          onSelectionChanged={onSelectionChanged}
          onModelUpdated={selectLoadedSelectedJobs}
          onCellClicked={handleCellClicked}
          onColumnVisible={handleColumnVisibilityChanged}
          onFilterChanged={handleFilterChanged}
          onSortChanged={onColumnSortChanged}
          enableCellTextSelection
          ensureDomOrder
          rowDragMultiRow
          sideBar={sideBar}
          rowModelType={"serverSide"}
          serverSideDatasource={datasource}
          pagination
          paginationPageSize={100}
          maxBlocksInCache={2} // SSRM ignores this under pagination mode; kept as intent signal
          animateRows
          gridOptions={JobGridOptions}
          maintainColumnOrder
          rowStyle={CustomRowStyle}
          onPaginationChanged={onPaginationChanged}
          noRowsOverlayComponent={BadDataOverlay}
          suppressMultiSort={!multipleSortEnabled}
        />
        <SelectedInfo>
          <Text>{selectedJobs.length} selected</Text>
          {selectedJobs.length > 0 && (
            <DangerButton
              data-testid="clear-selected-jobs-button"
              variant="minimal"
              intent="danger"
              onClick={handleClearAllSelectedJobs}
            >
              Clear
            </DangerButton>
          )}
        </SelectedInfo>
      </PanelContainer>
    </DispatchNotesProvider>
  );
};

export default JobsGrid;
